import { useEffect, useRef, useState } from "react";
import {
  getGoogleClientId,
  loadGoogleIdentityServices,
  initializeGoogleAuth,
  renderGoogleButton,
  GoogleButtonText,
} from "@utils/googleIdentity";

interface GoogleSignInProps {
  onSuccess: (response: any) => void;
  onError?: (error: string) => void;
  buttonId?: string;
  text?: GoogleButtonText;
  width?: number | string;
  className?: string;
  enabled?: boolean;
}

export const GoogleSignIn: React.FC<GoogleSignInProps> = ({
  onSuccess,
  onError,
  buttonId = "google-signin-button",
  text = "signup_with",
  width = 400,
  className = "",
  enabled = true,
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const onSuccessRef = useRef(onSuccess);
  onSuccessRef.current = onSuccess;

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return;
    }

    let cancelled = false;

    const setup = async () => {
      setLoading(true);
      setError("");

      const clientId = getGoogleClientId();
      if (!clientId) {
        const errorMsg =
          "Google sign-in is not configured. Please contact support.";
        if (!cancelled) {
          setError(errorMsg);
          onError?.(errorMsg);
          setLoading(false);
        }
        return;
      }

      try {
        await loadGoogleIdentityServices();
        if (cancelled) return;

        initializeGoogleAuth((response) => {
          onSuccessRef.current(response);
        });

        requestAnimationFrame(() => {
          if (cancelled) return;
          try {
            renderGoogleButton({ elementId: buttonId, text, width });
            setLoading(false);
          } catch (renderError) {
            const errorMsg = "Failed to render Google Sign-In button.";
            setError(errorMsg);
            onError?.(errorMsg);
            setLoading(false);
          }
        });
      } catch (err) {
        const errorMsg =
          err instanceof Error
            ? err.message
            : "Failed to load Google Sign-In.";
        if (!cancelled) {
          setError(errorMsg);
          onError?.(errorMsg);
          setLoading(false);
        }
      }
    };

    setup();

    return () => {
      cancelled = true;
    };
  }, [buttonId, text, width, enabled, onError]);

  if (!enabled) {
    return null;
  }

  return (
    <div className={`position-relative ${className}`}>
      <div id={buttonId} />
      {loading && (
        <div className="d-flex align-items-center justify-content-center gap-2">
          <span className="loading-dots">
            <span></span>
            <span></span>
            <span></span>
          </span>
        </div>
      )}
      {error && <div className="text-danger small mt-1">{error}</div>}
      <style>
        {`
          @keyframes bounce {
            0%, 80%, 100% {
              transform: scale(0);
            } 40% {
              transform: scale(1);
            }
          }
          .loading-dots {
            display: inline-flex;
            gap: 4px;
            align-items: center;
          }
          .loading-dots span {
            display: inline-block;
            width: 8px;
            height: 8px;
            border-radius: 50%;
            background-color: #05569f;
            animation: bounce 1.4s infinite ease-in-out both;
          }
          .loading-dots span:nth-child(1) {
            animation-delay: -0.32s;
          }
          .loading-dots span:nth-child(2) {
            animation-delay: -0.16s;
          }
          .loading-dots span:nth-child(3) {
            animation-delay: 0s;
          }
        `}
      </style>
    </div>
  );
};
